import Grid from "@mui/material/Grid2";
import Typography from "@mui/material/Typography";
import { useContext, useState } from "react";
import { APIContext, DialogAction } from "../../Contexts/APIContext";
import Line from "../../Utilities/Line";
import TableContainer from "@mui/material/TableContainer";
import ResponsiveTable from "../../Utilities/ResponsiveTable";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import { UserViewModel } from "./ViewModels/OrganisationVieModel";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import IconButton from "@mui/material/IconButton";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Button from "@mui/material/Button";
import AddOrgAdminDialog from "../../Licensing/Organisation/CreateOrgUserDialog";
import EditOrgUserDialog from "../../Licensing/Organisation/EditOrgUser";
import { AuthContext } from "../../Authorisation/AuthContext";

interface IProps {
    users: UserViewModel[];
    title: string;
    refresh: () => void;
    orgId: string;
    role: "Choose Role" | "Manager" | "Member"
    siteId?: string;
}

export default function OrgUserTable({ users, title, refresh, orgId, role, siteId }: IProps) {
    const { get, loading, showMessage, openDialog } = useContext(APIContext);
    const { userDetails } = useContext(AuthContext);
    const [editId, setEditId] = useState<string | null>(null);
    const [addId, setAddId] = useState<string | null>(null);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [menuUser, setMenuUser] = useState<UserViewModel | null>(null);
    const open = Boolean(anchorEl);

    const closeDialogs = () => {
        refresh();
        setEditId(null);
        setAddId(null);
    };

    const handleMenuOpen = (event: any, user: any) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setMenuUser(user);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setMenuUser(null);
    };

    const handleToggleArchive = async (
        event: any,
        user: UserViewModel,
    ) => {
        event.stopPropagation();
        handleMenuClose();
        openDialog(<Typography>"Are you sure you want to remove this user?"</Typography>, [
            new DialogAction("Confirm", () => {
                get(
                    "AdminUser",
                    `UpdateArchiveStatus?id=${user.id}`,
                    (success: any) => {
                        showMessage("success", "User status has been updated");
                        refresh();
                    },
                );
            }
            )]);

    };

    const handleResetPassword = async (
        event: any,
        user: UserViewModel,
    ) => {
        event.stopPropagation();
        handleMenuClose();
        const action = `SendResetPasswordEmail?email=${encodeURIComponent(user.email)}`;
        await get("AdminUser", action, (success: any) => {
            showMessage(
                "success",
                "Password reset email sent",
            );
        });
    };

    return (
        <Grid container>
            <Grid container size={12}>
                <Grid size={10}>
                    <Typography variant="h6">{title}</Typography>
                </Grid>
                <Grid size={2}>
                    <Button size={'small'} variant="contained" onClick={() => setAddId('')}>
                        {role === 'Manager' ? 'Add Manager' : 'Add Member'}
                    </Button>
                </Grid>
            </Grid>
            <Line />
            <TableContainer sx={{ width: "100%", maxHeight: 200 }}>
                <ResponsiveTable stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Email</TableCell>
                            {role === 'Member' &&
                                <TableCell>Role</TableCell>
                            }
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody sx={{ width: "100%" }}>
                        {users !== null &&
                            users.map((row) =>
                                <TableRow key={row.name} hover sx={{ cursor: "pointer" }}>
                                    <TableCell>{row.name}</TableCell>
                                    <TableCell>{row.email}</TableCell>
                                    {role === 'Member' &&
                                        <TableCell>{row.role}</TableCell>
                                    }
                                    <TableCell align='right'>
                                        <Grid container spacing={1} justifyContent='flex-end' alignItems="center" sx={{ flexWrap: 'nowrap' }}>
                                            <Grid>
                                                <IconButton size="small" disabled={loading} onClick={(event) => handleMenuOpen(event, row)} aria-label="Staff actions dropdown">
                                                    <MoreVertIcon />
                                                </IconButton>

                                            </Grid>
                                        </Grid>
                                    </TableCell>
                                </TableRow>)
                        }
                    </TableBody>
                </ResponsiveTable>
                <Menu
                    open={open}
                    onClose={handleMenuClose}
                    anchorEl={anchorEl}
                >
                    {menuUser?.email !== userDetails?.userEmail &&
                        <MenuItem
                            onClick={(event) => menuUser && handleToggleArchive(event, menuUser)}
                            disabled={loading}
                        >
                            {"Remove"}
                        </MenuItem>
                    }
                    <MenuItem
                        onClick={() => {
                            setEditId(menuUser?.id ?? '')
                            handleMenuClose()
                        }}
                        disabled={loading}
                    >
                        {"Edit"}
                    </MenuItem>

                    {menuUser?.emailConfirmed &&
                        <MenuItem
                            onClick={(event) => menuUser && handleResetPassword(event, menuUser)}
                            disabled={loading}
                        >
                            Reset Password
                        </MenuItem>
                    }
                </Menu>
                <AddOrgAdminDialog open={addId != null} onClose={closeDialogs} orgId={orgId} role={role} siteId={siteId} />
                <EditOrgUserDialog open={editId != null} onClose={closeDialogs} userId={editId} orgId={orgId} showRoles={false} />
            </TableContainer>
        </Grid>
    );
}
